// ink-component
import React, { ChangeEvent, MouseEvent, useContext, useEffect, useState } from 'react';

import defineInitialValue from './helpers/defineInitialValue';
import DeprecatedDropdownItem from '../../deprecated/DeprecatedDropdown/DeprecatedDropdownItem/DeprecatedDropdownItem';

import TableContext from '../Table/TableContext/TableContext';

import RadioOption, { RadioOptionProps } from './RadioOption/RadioOption';

import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';
import getGlobalProps from '../../helpers/constants/global-props';

export type RadioOptionType = Pick<
  RadioOptionProps,
  'id' | 'value' | 'label' | 'isChecked' | 'isInvalid' | 'isDisabled' | 'name'
>;

export type Meta = {
  initial?: string;
};

export type Input = {
  onBlur?: () => void;
  onChange?: () => void;
  onClick?: () => void;
  onFocus?: () => void;
  value?: string;
};

export interface RadioProps {
  /** Radio identifier */
  id: string;
  /** { DEPRECATED -
   * The `option.isChecked` prop is deprecated and will be removed - use `value` } -
   * Options array that will be used to create each Radio Button */
  options: RadioOptionType[];
  /** External control and initial value */
  value?: string;
  /* HTML Name prop */
  name?: string;
  /** Size modifier */
  size?: 'full' | 'large' | 'half' | 'quarter' | 'auto';
  /** Modifier for tables */
  isCell?: boolean;
  /** Modifier for inline */
  isInline?: boolean;
  /** Adapts the Radio component to be used inside Dropdowns */
  isDropdown?: boolean;
  /** onChange callback. Passes the Event */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** onClick callback. Passes the Event.
   * **Attention:** you should always give preference to the onChange event
   * because it is triggered by both mouse and keyboard events and is more
   * reliable than the onClick for value control. Always use the onClick callback
   * in combination with the onChange, and **just if you need to execute a
   * function if the current value is clicked again.**
   * */
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  /** HTML type */
  autoFocus?: boolean;
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;

  /** @ignore */
  dataPrivate?: boolean;
  /** @ignore */
  isTable?: boolean;
  /** @ignore */
  meta?: Meta;
  /** @ignore */
  input?: Input;
  /** @ignore */
  defaultValue?: string;
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @categories form
 * @purpose Select one answer from a small and visible set of options
 * @deprecated This component is deprecated and replaced by [NewRadio](https://ink.carta.com/components/NewRadio/)
 * @status deprecated
 * @replacementComponent NewRadio
 * @dateOfDeletion Septermber 1st, 2023
 */
function Radio({ meta = {}, input = {}, ...props }: RadioProps) {
  useDeprecatedWarning({ componentName: 'Radio', substitute: 'NewRadio' });

  const { tableId } = useContext(TableContext);
  const checkedOption = props.options.find(option => option.isChecked);
  const reduxFormValue = input.value || meta.initial;

  const [value, setValue] = useState(
    defineInitialValue(reduxFormValue, props.defaultValue, props.value, checkedOption),
  );

  useEffect(() => {
    if (input.value) {
      setValue(input.value);
    } else if (props.value) {
      setValue(props.value);
    }
  }, [input.value, props.value]);

  useEffect(() => {
    if (props.isTable) {
      const checkedOption = props.options.find(option => option.isChecked);
      if (checkedOption) {
        setValue(checkedOption.value);
      } else {
        setValue(null);
      }
    }
  }, [props.options, props.isTable]);

  const toggleChecked = (e: ChangeEvent<HTMLInputElement>) => {
    if (props.onChange) {
      props.onChange(e);
    }
    setValue(e.target.value);
  };

  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    if (props.onClick) {
      props.onClick(e);
    }
  };

  const renderRadios = () => {
    /* TODO: The option.isChecked prop must be removed so we only have the value */
    /* prop this is here for backwards compatibility */
    return props.options.map((option, index) => {
      const radioOptionProps = {
        key: option.id,
        id: option.id,
        name: props.id,
        label: option.label,
        value: option.value,
        size: props.size,
        isInvalid: option.isInvalid,
        isInline: props.isInline,
        isDisabled: option.isDisabled,
        isChecked: option.value === value,
        onChange: toggleChecked,
        onClick: handleClick,
        input,
        isCell: props.isCell || Boolean(tableId),
        isFieldless: props.isDropdown,
        autoFocus: props.autoFocus && index === 0,
        dataPrivate: props.dataPrivate,
      };

      return props.isDropdown ? (
        <DeprecatedDropdownItem key={option.id}>
          <RadioOption {...radioOptionProps} />
        </DeprecatedDropdownItem>
      ) : (
        <RadioOption {...radioOptionProps} />
      );
    });
  };

  return (
    <div className="radio" data-testid={props['data-testid']} aria-label={props['aria-label']} {...globalProps}>
      {renderRadios()}
    </div>
  );
}

Radio.defaultProps = {
  value: undefined,
  name: undefined,
  size: 'large',
  isCell: false,
  isInline: false,
  isDropdown: false,
  onChange: undefined,
  onClick: undefined,
  autoFocus: false,

  dataPrivate: undefined,
  isTable: false,
  meta: {},
  input: {},
  defaultValue: undefined,
};

Radio.displayName = 'Radio';

export default Radio;
